
import { useStytchMember } from "@stytch/react/b2b";
import moment from "moment-timezone";
import { useCreateKnowledgeReport, fetchKnowledgeItemsPage, KnowledgeApi, useGetCommonBlocks, useGetKnowledgeTags, useKnowledgeItemsPaginated, useGetKnowledgeTagsBreakdown, useListOwnersAssignedUsersTasks, ListOwnerAssignments } from "@/lib/api/knowledge";
import { cn, CommonDestinationTimezone, GenerateLoadingOrError } from "@/lib/utils";
import { ArrowLeft, CheckCircle, ChevronDown, ChevronRight, Clock, PenSquare, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import React, { useMemo, useState } from "react";
import { KnowledgeTaskCard } from "./components/KnowledgeTaskCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { Loader } from "@/components/ui/loader";
import { useQueryClient } from "@tanstack/react-query";
import { TagsManagementView } from "./components/TagsManagementView";
import { KnowledgeCommonBlockCard } from "./components/KnowledgeCommonBlockCard";
import { useToast } from "@/components/ui/use-toast";
import { downloadFromS3 } from "@/lib/file";
import { Separator } from "@/components/ui/separator";
import { PermissionType, PossiblePackage } from "lhc_permissions";
import { hasAccessToFeature } from "@/lib/models/auth";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { KnowledgeSearchComponent } from "./components/KnowledgeSearchComponent";

function generateFilterFromTab(tab: string) {
  switch (tab) {
    case "assigned":
      return "assigned";
    case "owned":
      return "owned";
    case "all":
      return "all";
    case "resources":
      return "common-blocks";
    default:
      return "all";
  }
}

export function KnowledgeTasksDashboard() {
  const { member } = useStytchMember();
  const navigate = useNavigate();
  const [tab, setTab] = useState("assigned");
  const [page, setPage] = useState(1);
  const [auditFilterDate, setAuditFilterDate] = useState(moment.tz(CommonDestinationTimezone).subtract(6, "months").startOf("day").toDate());
  const knowledgeItemsRes = useKnowledgeItemsPaginated({
    params: {
      page,
      filter: generateFilterFromTab(tab),
      last_audit_date: tab === "audit-review" ? { before: auditFilterDate } : undefined,
    },
    queryFn: fetchKnowledgeItemsPage,
    enabled: tab !== "tags",
  });

  const hasManagerPermission = hasAccessToFeature({
    name: "Knowledge Tasks",
    requiredPermissions: [PermissionType.Manager],
    requiredPackages: [PossiblePackage.KnowledgeTasks],
  }, member);

  const user_id = member?.trusted_metadata.db_user_id;
  const member_role = member?.trusted_metadata.role;
  const isAdmin = member_role === "admin";
  const canSeeOtherTasks = isAdmin || hasManagerPermission;


  const loadingOrErrorUi = GenerateLoadingOrError(tab !== 'tags' ? [knowledgeItemsRes] : []);

  if (loadingOrErrorUi && tab !== 'tags') {
    return (
      <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
        <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
          Tasks
        </h1>
        {loadingOrErrorUi}
      </section>
    );
  }

  const knowledgeItems = tab !== 'tags' ? (knowledgeItemsRes?.data?.data?.data || []) : [];

  const tasks = knowledgeItems.filter((item: KnowledgeApi.KnowledgeItem) => item.item_type === "task");
  const commonBlocks = knowledgeItems.filter((item: KnowledgeApi.KnowledgeItem) => item.item_type === "common-block");
  const assignedTasks = tasks.filter((item: KnowledgeApi.KnowledgeItem) => {
    return item?.assignments?.some((assignment: KnowledgeApi.KnowledgeAssignment) => assignment.assignee_id === user_id);
  });
  const ownedTasks = tasks.filter((item: KnowledgeApi.KnowledgeItem) => item.owner_id === user_id);
  const allTasks = tasks;

  const PaginationControls = ({ res }: { res: typeof knowledgeItemsRes }) => {
    const isLoading = res.isLoading;
    const info = res?.data?.data?.pagination || {};

    return (
      <div className="flex items-center justify-between mt-4">
        <Button
          onClick={() => setPage(page - 1)}
          disabled={page <= 1 || isLoading}
        >
          {isLoading ? <Loader className="w-4 h-4" /> : <span>Previous</span>}
        </Button>

        <span>
          Page {info.currentPage || 1} of {info.totalPages || 1}
        </span>

        <Button
          onClick={() => setPage(page + 1)}
          disabled={page >= (info.totalPages || 1) || isLoading}
        >
          {isLoading ? <Loader className="w-4 h-4" /> : <span>Next</span>}
        </Button>
      </div>);
  }

  const onTabChange = (value: string) => {
    setTab(value);
    setPage(1);
  };

  return (
    <section className="flex min-h-full flex-1 flex-col justify-start px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
          Knowledge Center
        </h1>
        {tab !== 'tags' && (
          <Button
            onClick={() => navigate("/tasks/create")}
            className="flex items-center gap-2"
          >
            <PlusCircle className="h-4 w-4" />
            Create Task
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={onTabChange} className="space-y-6">
        <TabsList>
          <TabsTrigger value="assigned">Assigned To Me</TabsTrigger>
          <TabsTrigger value="owned">Owned Tasks</TabsTrigger>
          {canSeeOtherTasks && <TabsTrigger value="all">All Tasks</TabsTrigger>}
          {canSeeOtherTasks && <TabsTrigger value="tags">View Tags</TabsTrigger>}
          <TabsTrigger value="resources">Resources</TabsTrigger>
          <TabsTrigger value="audit-review">Audit Review</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="assigned_out">Assigned Out</TabsTrigger>
        </TabsList>

        <TabsContent value="assigned" className="space-y-4">
          {knowledgeItemsRes.isLoading && tab === 'assigned' ? <Loader /> : assignedTasks.length > 0 ? (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {assignedTasks.map((task: KnowledgeApi.KnowledgeItem) => (
                  <KnowledgeTaskCard
                    key={`assigned-${task.id}`}
                    task={task}
                    isAssigned={true}
                  />
                ))}
              </div>
              <PaginationControls res={knowledgeItemsRes} />
            </>
          ) : (
            <div className="border rounded-lg p-6 bg-background text-center">
              <h3 className="font-medium mb-2">No assigned tasks</h3>
              <p className="text-sm text-muted-foreground">
                You don't have any tasks assigned to you yet.
              </p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="owned" className="space-y-4">
          {knowledgeItemsRes.isLoading && tab === 'owned' ? <Loader /> : ownedTasks.length > 0 ? (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {ownedTasks.map((task: KnowledgeApi.KnowledgeItem) => (
                  <KnowledgeTaskCard
                    key={`owned-${task.id}`}
                    task={task}
                    isAssigned={false}
                    assignmentCount={task.assignmentsCount || 0}
                  />
                ))}
              </div>
              <PaginationControls res={knowledgeItemsRes} />
            </>
          ) : (
            <div className="border rounded-lg p-6 bg-background text-center">
              <h3 className="font-medium mb-2">No owned tasks</h3>
              <p className="text-sm text-muted-foreground">
                You haven't created any tasks yet.
              </p>
            </div>
          )}
        </TabsContent>
        {canSeeOtherTasks && (
          <TabsContent value="all" className="space-y-4">
            <AllTasksView currentPage={page} onPageChange={setPage} />
          </TabsContent>
        )}
        {canSeeOtherTasks && (
          <TabsContent value="tags" className="space-y-4">
            <TagsManagementView />
          </TabsContent>
        )}
        <TabsContent value="resources" className="space-y-4">
          {knowledgeItemsRes.isLoading && tab === 'resources' ? <Loader /> : commonBlocks.length > 0 ? (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {commonBlocks.map((task: KnowledgeApi.KnowledgeItem) => (
                  <KnowledgeTaskCard
                    key={`common-${task.id}`}
                    task={task}
                    isAssigned={false}
                    assignmentCount={task.assignmentsCount || 0}
                  />
                ))}
              </div>
              <PaginationControls res={knowledgeItemsRes} />
            </>
          ) : (
            <div className="border rounded-lg p-6 bg-background text-center">
              <h3 className="font-medium mb-2">No Resources</h3>
              <p className="text-sm text-muted-foreground">
                You haven't created any resources yet.
              </p>
            </div>
          )}
        </TabsContent>
        <TabsContent value="audit-review" className="space-y-4">
          {knowledgeItemsRes.isLoading && tab === 'audit-review' ? <Loader /> : tasks.length > 0 ? (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {tasks.map((task: KnowledgeApi.KnowledgeItem) => (
                  <KnowledgeTaskCard
                    key={`audit-review-${task.id}`}
                    task={task}
                    isAssigned={false}
                    assignmentCount={task.assignmentsCount || 0}
                  />
                ))}
              </div>
              <PaginationControls res={knowledgeItemsRes} />
            </>
          ) : (
            <div className="border rounded-lg p-6 bg-background text-center">
              <h3 className="font-medium mb-2">No Tasks Require Review</h3>
              <p className="text-sm text-muted-foreground">
                Checked all tasks requiring an review before {moment.tz(auditFilterDate, CommonDestinationTimezone).format("MM/DD/YYYY")}
              </p>
            </div>
          )}
        </TabsContent>
        <TabsContent value="reports" className="space-y-4">
          <KnowledgeAuditReports />
        </TabsContent>
        <TabsContent value="assigned_out" className="space-y-4">
          <OwnersAssignedOutReport />
        </TabsContent>
      </Tabs>
    </section>
  );
}

function AllTasksView({ currentPage, onPageChange }: { currentPage: number; onPageChange: (page: number) => void }) {
  const queryClient = useQueryClient();
  const [activeTagId, setActiveTagId] = useState<"no-tag" | string | null>(null);
  const [mainCategoryId, setMainCategoryId] = useState<string | null>(null);
  const [secondaryCategoryId, setSecondaryCategoryId] = useState<string | null>(null);
  const tagsBreakdownRes = useGetKnowledgeTagsBreakdown();
  const knowledgeItemsRes = useKnowledgeItemsPaginated({
    keyOverride: "all-tasks",
    params: {
      page: currentPage,
      filter: "all",
      activeTagId: undefined,
      emptyTags: activeTagId === "no-tag",
      main_category_id: mainCategoryId,
      secondary_category_id: secondaryCategoryId,
    },
    queryFn: fetchKnowledgeItemsPage,
    enabled: (!!mainCategoryId || !!secondaryCategoryId),
  });

  const tasks = (knowledgeItemsRes?.data?.data?.data || []);

  const tags = useMemo(() => {
    const base = tagsBreakdownRes?.data?.data?.tags || [];
    const mainIds = tagsBreakdownRes?.data?.data?.main_category_ids || [];
    const secondaryIdsBreakdown = tagsBreakdownRes?.data?.data?.main_to_secondary_id_breakdown || {};

    if (!mainCategoryId) {
      return base.filter((item: { id: string }) => mainIds.includes(item.id))
    }

    if (mainCategoryId && !secondaryCategoryId) {
      return base.filter((item: { id: string }) => ((secondaryIdsBreakdown || {})[mainCategoryId] || []).includes(item.id))
    }

    return []
  }, [tagsBreakdownRes, mainCategoryId, secondaryCategoryId]);

  const loadingOrErrorUi = GenerateLoadingOrError([activeTagId ? knowledgeItemsRes : undefined, tagsBreakdownRes].filter(Boolean));

  if (loadingOrErrorUi) {
    return (loadingOrErrorUi);
  }

  const handleTagSelect = (tagId: string) => {
    setActiveTagId(tagId);
    onPageChange(1);
  };

  const handleSelectMainCategory = (tagId: string | null) => {
    setMainCategoryId(tagId);
    onPageChange(1);
  };

  const handleSelectSubCategory = (tagId: string | null) => {
    setSecondaryCategoryId(tagId);
    onPageChange(1);
  };

  const handleClearTag = () => {
    setActiveTagId(null);
    onPageChange(1);
  }


  if (mainCategoryId || secondaryCategoryId) {
    return <CategoryTagDiveDown
      onSelectMain={handleSelectMainCategory} onSelectSecondary={handleSelectSubCategory} mainCategoryId={mainCategoryId} secondaryCategoryId={secondaryCategoryId} tags={tags}
      currentPage={currentPage}
      onPageChange={onPageChange}
      knowledgeItemsRes={knowledgeItemsRes}
      handleClearTag={handleClearTag}
      tasks={tasks}
    />
  }

  if (!activeTagId && activeTagId !== "no-tag") {
    return (
      <>
        <RenderTagList
          onSelect={handleSelectMainCategory}
          tags={tags} title="Drill down by Main Category"
          hasBack={false}
        />
        <Separator className="my-6" />
        <KnowledgeSearchComponent />
      </>
    )
  }

  return (<TagDiveDown tasks={tasks} currentPage={currentPage} onPageChange={onPageChange} knowledgeItemsRes={knowledgeItemsRes} handleClearTag={handleClearTag} />)
}

function CategoryTagDiveDown({
  handleClearTag, tasks, currentPage, onPageChange, knowledgeItemsRes, onSelectMain, onSelectSecondary, mainCategoryId, secondaryCategoryId, tags
}: {
  onSelectSecondary: (item: string | null) => any;
  onSelectMain: (item: string | null) => any;
  tags: KnowledgeApi.KnowledgeTaskTag[];
  mainCategoryId?: string | null;
  secondaryCategoryId?: string | null;
  currentPage: number;
  tasks: any,
  knowledgeItemsRes: any;
  onPageChange: (page: number) => void;
  handleClearTag: () => void;
}) {
  if (mainCategoryId && !secondaryCategoryId) {
    return (
      <>
        <RenderTagList onSelect={onSelectSecondary} tags={tags.filter((tag: KnowledgeApi.KnowledgeTaskTag) => tag.id !== mainCategoryId)} title="Drill down by Sub category" hasBack={true} onBack={() => onSelectMain(null)} />
        <TagDiveDown tasks={tasks} currentPage={currentPage} onPageChange={onPageChange} knowledgeItemsRes={knowledgeItemsRes} />
      </>
    );
  }

  return (<TagDiveDown tasks={tasks} currentPage={currentPage} onPageChange={onPageChange} knowledgeItemsRes={knowledgeItemsRes} handleClearTag={() => onSelectSecondary(null)} />)
}

function TagDiveDown({
  tasks,
  knowledgeItemsRes,
  currentPage,
  onPageChange,
  handleClearTag,
}: {
  currentPage: number;
  tasks: any,
  knowledgeItemsRes: any;
  onPageChange: (page: number) => void;
  handleClearTag?: () => void;
}) {
  if (!knowledgeItemsRes.isLoading && !knowledgeItemsRes.isFetching && !tasks?.length) {
    return (<div className="p-6 bg-background flex flex-col ">
      <div className="flex flex-row items-center justify-start">
        <Button variant="ghost" size="sm" className="w-[120px]" onClick={handleClearTag}><ArrowLeft className="h-4 w-4 mr-1" /> Clear Tag</Button>
        <h3 className="font-medium text-center ml-2">No tasks found for this tag</h3>
      </div>
    </div>)
  }

  const PaginationControls = () => {
    const isLoading = knowledgeItemsRes.isLoading || knowledgeItemsRes.isFetching;
    const info = knowledgeItemsRes?.data?.data?.pagination || {};

    return (
      <div className="flex items-center justify-between mt-4">
        <Button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1 || isLoading}
          className={cn(isLoading ? "flex items-center justify-center" : "")}
        >
          {isLoading ? <Loader className="w-4 h-4" /> : <span>Previous</span>}
        </Button>

        <span>
          Page {info.currentPage || 1} of {info.totalPages || 1}
        </span>

        <Button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= (info.totalPages || 1) || isLoading}
          className={cn(isLoading ? "flex items-center justify-center" : "")}
        >
          {isLoading ? <Loader className="w-4 h-4 px-2" /> : <span>Next</span>}
        </Button>
      </div>);
  };

  return (
    <>
      {handleClearTag && (<Button variant="ghost" size="sm" className="w-[120px]" onClick={handleClearTag}><ArrowLeft className="h-4 w-4 mr-1" /> Clear Tag</Button>)}
      {(knowledgeItemsRes.isLoading || knowledgeItemsRes.isFetching) && (
        <div className="flex items-center justify-center p-4">
          <Loader className="w-6 h-6" />
        </div>
      )}
      {
        !knowledgeItemsRes.isFetching && !knowledgeItemsRes.isLoading && tasks.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {tasks.map((task: KnowledgeApi.KnowledgeItem) => (
              <KnowledgeTaskCard
                key={`all-${task.id}`}
                task={task}
                isAssigned={false}
              />
            ))}
          </div>
        )}
      <PaginationControls />
    </>
  );
}

function RenderTagList({ onSelect, tags, title, hasBack, onBack }: { onSelect: (item: string | null) => any; tags: KnowledgeApi.KnowledgeTaskTag[]; title: string; hasBack?: boolean; onBack?: (item: any) => void }) {
  return (
    <div className="border rounded-lg p-6 bg-background text-center">
      <span className="flex flex-row justify-start items-center mb-3">
        {hasBack && (
          <Button onClick={onBack} variant={"ghost"} size="sm"><ArrowLeft className="w-4 h-4" /></Button>
        )}
        <h3 className="font-medium">{title}</h3>
      </span>
      <div className="flex flex-wrap gap-2 justify-start">
        {tags.map((tag: KnowledgeApi.KnowledgeTaskTag) => (
          <Button
            key={tag.id}
            variant="outline"
            size="sm"
            onClick={() => onSelect(tag.id)}
            className="px-3 py-1 h-auto"
          >
            {tag.name}
          </Button>
        ))}
        {tags.length === 0 && (
          <p className="text-sm text-muted-foreground">No tags available</p>
        )}
      </div>
    </div>
  );
}

function ViewTagCardsWithPagination() {

}


function KnowledgeAuditReports() {
  const { toast } = useToast();
  const [link, setLink] = useState<string | undefined>();
  const [isGenerating, setIsGenerating] = useState(false);
  const createReport = useCreateKnowledgeReport();

  const onGenerateReport = async () => {
    try {
      if (isGenerating) { return; }
      setIsGenerating(true);

      const result = await createReport();
      if (!result.success) {
        throw result;
      }

      setLink(result.data.link);
      downloadFromS3(result.data.link, "knowledge-audit-report.xlsx");

      toast({
        title: "Generated",
        description: "",
      });
    } catch (error) {
      setLink(undefined);
      console.error("Failed to generate knowledge item report", error);
      toast({
        title: "Failed to generate report",
        description: "",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="font-medium">Knowledge Audit Reports</h3>
        <p className="text-sm text-muted-foreground">
          Generate a report of all knowledge items in the system, that <span className="italic">you</span> have access to.
        </p>

      </div>
      <div className="flex flex-col gap-4 md:grid md:grid-cols-4">
        <Card>
          <CardHeader>
            <CardTitle>
              List Tasks Report
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button onClick={onGenerateReport} disabled={isGenerating}>
              {isGenerating ? "Generating..." : "Generate Report"}
            </Button>
            {link && !isGenerating && (
              <div className="flex flex-col gap-1 my-2">
                <Separator className="my-2" />
                <p className="text-sm text-muted-foreground">
                  Report generated successfully.
                </p>
                <a href={link} target="_blank" rel="noopener noreferrer" className="mt-2 text-xs text-muted-foreground">Click here to view report, if it doesn't download automatically.</a>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

type ExpandedState = Record<string, boolean>;

function OwnersAssignedOutReport() {
  const navigate = useNavigate();
  const assignedOutListRes = useListOwnersAssignedUsersTasks();
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const { users, usersMap, assignmentsByUser } = useMemo(() => {
    if (!assignedOutListRes.data?.success || !assignedOutListRes.data?.data) {
      return {};
    }

    const assignments = assignedOutListRes.data.data || [];
    const assignmentsByUser: Record<string, ListOwnerAssignments[]> = {};
    const usersMap: Record<string, { name: string }> = {};

    assignments.forEach((assignment: ListOwnerAssignments) => {
      const assignee_id: string = assignment.assignee_id;
      if (!usersMap[assignee_id]) {
        usersMap[assignee_id] = assignment.Assignee;
      }
      if (!assignmentsByUser[assignee_id]) {
        assignmentsByUser[assignee_id] = [];
      }
      assignmentsByUser[assignee_id].push(assignment);
    });

    // Sort profiles within each customer group by commodity and then crop year
    Object.values(assignmentsByUser).forEach(assignments => {
      return assignments.sort((a, b) => {
        if (a.Assignee.name !== b.Assignee.name) {
          return a.Assignee.name.localeCompare(b.Assignee.name);
        }
        return a.KnowledgeItem.title.localeCompare(b.KnowledgeItem.title);
      });
    });

    const users = Object.entries(usersMap).sort((a, b) => {
      return a[1].name.localeCompare(b[1].name);
    }).map(([id, value]) => ({ ...value, id }));

    return { assignmentsByUser, users, usersMap };
  }, [assignedOutListRes.data?.data || []]);

  const loadingOrErrorUi = GenerateLoadingOrError([assignedOutListRes]);
  if (loadingOrErrorUi) return loadingOrErrorUi;


  const toggleExpanded = (id: string) => {
    setExpanded(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };




  return (
    <div className="p-4 space-y-6">
      <div className="flex flex-col lg:flex-row gap-4 lg:justify-between ">
        <div className="p-3 border rounded-md space-y-3">
          <span className="flex flex-row justify-between">
            <h2 className="text-xl font-semibold">Assigned Out Tasks</h2>
          </span>
        </div>
      </div>
      <ScrollArea className="h-[550px]">
        <Table className="risk-compact-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[20px] py-1"></TableHead>
              <TableHead className="py-1">Assignee</TableHead>
              <TableHead className="py-1"></TableHead>
              <TableHead className="py-1"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(users?.length ?? 0) === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-4">
                  No Assigned Out Tasks
                </TableCell>
              </TableRow>
            ) : (
              users!.map((user: { name: string; id: string; }) => (
                <React.Fragment key={user.id}>
                  <TableRow
                    className="cursor-pointer hover:bg-gray-50 customer-row"
                    onClick={() => toggleExpanded(user.id)}
                  >
                    <TableCell>
                      {expanded[user.id] ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </TableCell>
                    <TableCell className="font-medium text-sm">{user.name}</TableCell>
                    <TableCell className="text-sm"></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  {expanded[user.id] && (
                    <TableRow>
                      <TableCell colSpan={5} className="p-0">
                        <div className="bg-gray-50  expanded-row-content">
                          <Table className="risk-compact-table">
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-[200px] py-1 text-xs"></TableHead>
                                <TableHead className="py-1 text-xs">Task</TableHead>
                                <TableHead className="py-1 text-xs">Status</TableHead>
                                <TableHead className="py-1 text-xs"></TableHead>

                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {assignmentsByUser![user.id]!.map((assignment: ListOwnerAssignments) => {
                                return (
                                  <TableRow key={assignment.id} >
                                    <TableCell className="text-xs"></TableCell>
                                    <TableCell className="text-xs" onClick={() => navigate(`/tasks/${assignment.knowledge_item_id}`)}><span className="underline decoration-solid decoration-sky-500">{assignment.KnowledgeItem.title}</span></TableCell>
                                    <TableCell className="text-xs">
                                      <Badge
                                        variant="outline"
                                        className={
                                          assignment.status === 'COMPLETED'
                                            ? "bg-green-50 text-green-700 border-green-200"
                                            : assignment.status === 'IN_PROGRESS'
                                              ? "bg-blue-50 text-blue-700 border-blue-200"
                                              : "bg-amber-50 text-amber-700 border-amber-200"
                                        }
                                      >
                                        {assignment.status === 'COMPLETED' && <CheckCircle className="h-3 w-3 mr-1" />}
                                        {assignment.status === 'IN_PROGRESS' && <PenSquare className="h-3 w-3 mr-1" />}
                                        {assignment.status === 'PENDING' && <Clock className="h-3 w-3 mr-1" />}
                                        {assignment.status.replace('_', ' ')}
                                      </Badge>
                                    </TableCell>

                                    <TableCell>
                                    </TableCell>
                                  </TableRow>
                                )
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </ScrollArea>
    </div >
  )
}
